import React, { useEffect, useState } from 'react';

import Loader from '@components/loader/Loader';
import { CDN_URL } from '@utils';

import CollapsibleSection from '../../common/CollapsibleSection';
import WidgetPaper from '../display-widget/WidgetPaper';
import WidgetSection from '../display-widget/WidgetSection';
import DynamicField from './DynamicField';
import {
  extractAllPropertiesFromSections,
  extractValuesFromDynamicPayload,
  IDynamicDisplayWidgetProps,
  IEnhancedDynamicDisplay,
  ISimplifiedDynamicSection
} from '.';

import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles<{
  hasFourFields: boolean;
  hasThreeFields: boolean;
  noGap: boolean;
}>()((theme, { hasFourFields, hasThreeFields, noGap }) => ({
  drawerContainer: {
    padding: '10px'
  },
  actionBar: {
    margin: 0
  },
  property: {
    minWidth: 100,
    width: hasFourFields
      ? '24% !important'
      : hasThreeFields
        ? 'calc((100% - 30px) / 3) !important'
        : '32% !important'
  },
  fullWidthProperty: {
    minWidth: 100,
    width: '100% !important'
  },
  rowBreak: {
    flexBasis: '100%',
    width: 0,
    height: 0,
    padding: 0,
    margin: 0,
    border: 0
  },
  drawerProp: {
    width: '100%'
  },
  propContainer: {
    padding: '23px 0 0 0',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    overflow: 'visible',
    ...(noGap ? {} : { gap: '15px' })
  },
  sectionContainer: {
    padding: '0 0 23px 0',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    overflow: 'visible'
  },
  contentContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    overflow: 'visible'
  },
  actionsSection: {
    width: '100%',
    textAlign: 'center',
    margin: '22px 0 0'
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%'
  }
}));

const renderProperties = (
  properties: string[],
  allProperties: Record<string, IEnhancedDynamicDisplay>,
  values: Record<string, string>,
  classes: Record<string, string>,
  currencySymbol?: string
) => {
  const renderedSections: Record<string, boolean> = {};

  const content: React.ReactNode[] = [];

  // Find the last sectionTitle in the list
  let lastSectionTitle: string | null = null;
  for (let i = properties.length - 1; i >= 0; i--) {
    const prop = allProperties[properties[i]];
    if (prop?.sectionTitle) {
      lastSectionTitle = prop.sectionTitle;
      break;
    }
  }

  properties.forEach((key) => {
    const prop = allProperties[key];
    if (!prop) return;

    if (prop.sectionTitle) {
      // Render section only once
      if (!renderedSections[prop.sectionTitle]) {
        const sectionFields = properties
          .filter((k) => allProperties[k]?.sectionTitle === prop.sectionTitle)
          .map((k) => renderField(allProperties[k]));
        const isLastSection = prop.sectionTitle === lastSectionTitle;

        content.push(
          <WidgetSection key={prop.sectionTitle} title={prop.sectionTitle}>
            <div
              className={
                !isLastSection
                  ? classes.sectionContainer
                  : classes.contentContainer
              }
            >
              {sectionFields}
            </div>{' '}
          </WidgetSection>
        );
        renderedSections[prop.sectionTitle] = true;
      }
    } else {
      // Render inline immediately
      content.push(renderField(prop));
    }
  });

  return <>{content}</>;

  function renderField(curProperty: IEnhancedDynamicDisplay) {
    const baseClass = curProperty.fullLine
      ? classes.fullWidthProperty
      : classes.property;

    const field = (
      <DynamicField
        key={curProperty.name}
        astAccountName=""
        property={curProperty}
        value={values[curProperty.name] ?? ''}
        className={clsx(baseClass)}
        cdnUrl={CDN_URL}
        tooltip={curProperty.tooltip}
        currencySymbol={currencySymbol}
      />
    );

    if (!curProperty.fullLine && curProperty.breaksRow) {
      return (
        <React.Fragment key={curProperty.name}>
          {field}
          <span className={classes.rowBreak} />
        </React.Fragment>
      );
    }

    return field;
  }
};

const DynamicWidget: React.FC<IDynamicDisplayWidgetProps> = ({
  view,
  actions,
  currencySymbol,
  hasFourFields = false,
  hasThreeFields = false,
  noGap = false,
  isCollapsible = false,
  loading = false,
  collapsibleSectionTitle = 'Widget',
  onButtonClick
}) => {
  const { classes } = useStyles({ hasFourFields, hasThreeFields, noGap });
  const [ready, setReady] = useState<boolean>(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [viewSections, setViewSections] = useState<ISimplifiedDynamicSection[]>(
    []
  );
  const [allProperties, setAllProperties] = useState<
    Record<string, IEnhancedDynamicDisplay>
  >({});

  useEffect(() => {
    const { extSections, extProperties } = extractAllPropertiesFromSections(
      view.sections
    );
    const extValues = extractValuesFromDynamicPayload(extProperties);

    setViewSections(extSections);
    setValues(extValues);
    setAllProperties(extProperties);
    setReady(true);
  }, [view.sections]);

  function renderDynamicWidget() {
    return (
      <>
        {isCollapsible && renderWidgetSections()}
        {!isCollapsible && <WidgetPaper>{renderWidgetSections()}</WidgetPaper>}
      </>
    );
  }

  function renderWidgetSections() {
    return ready ? (
      <div>
        {viewSections.map((section, index, array) => {
          if (section.hidden) return null; // Skip hidden sections

          // Determine if the section should be wrapped in WidgetSection
          const shouldShowSection =
            section.showSection !== undefined ? section.showSection : true;

          // Render the properties using the helper function
          const propertiesContent = renderProperties(
            section.properties,
            allProperties,
            values,
            classes,
            currencySymbol
          );

          return shouldShowSection ? (
            <WidgetSection
              key={section.name}
              classes={{ container: classes.propContainer }}
              title={isCollapsible ? '' : section.title}
              useSeparator={index < array.length - 1}
              showActionButton={section.showActionButtons && !isCollapsible}
              actions={isCollapsible ? null : actions}
              middle={section.middle}
              isCollapsible={isCollapsible}
              collapsibleSectionTitle={collapsibleSectionTitle}
            >
              {loading ? (
                <div className={classes.loader}>
                  <Loader />
                </div>
              ) : (
                propertiesContent
              )}
            </WidgetSection>
          ) : (
            <div key={section.name} className={classes.propContainer}>
              {loading ? (
                <div className={classes.loader}>
                  <Loader />
                </div>
              ) : (
                propertiesContent
              )}
            </div>
          );
        })}
      </div>
    ) : (
      <></>
    );
  }

  return isCollapsible ? (
    <CollapsibleSection
      title={collapsibleSectionTitle}
      actions={actions}
      onButtonClick={onButtonClick}
    >
      {renderDynamicWidget()}
    </CollapsibleSection>
  ) : (
    renderDynamicWidget()
  );
};

export default DynamicWidget;
